import { ItemUnit, MessageType, Settings, TableShape, User } from '@backend/domain'
import {
	CategoryRepository,
	MenuItemCategoryRepository,
	MenuItemRepository,
	SettingsRepository,
	TableRepository,
	TableSubscriberRepository,
	TableTokenRepository,
} from '@backend/repository'
import { Injectable, Logger } from '@nestjs/common'
import { QuickAction } from '../../settings'
import { CreateTenantWithUserRequest } from '../types'

@Injectable()
export class TenantSeedService {
	private readonly logger = new Logger(TenantSeedService.name)

	constructor(
		private readonly _tableRepository: TableRepository,
		private readonly _settingsRepository: SettingsRepository,
		private readonly _categoryRepository: CategoryRepository,
		private readonly _menuItemRepository: MenuItemRepository,
		private readonly _tableTokenRepository: TableTokenRepository,
		private readonly _itemCategoryRepository: MenuItemCategoryRepository,
		private readonly _tableSubscriberRepository: TableSubscriberRepository,
	) {}

	async seed(tenantId: string, user: User, request: CreateTenantWithUserRequest): Promise<void> {
		this.logger.debug(`Seeding data for ${tenantId}`)
		await this.seedSettings(tenantId, request)
		await this.seedTables(tenantId, user)
		await this.seedMenu(tenantId, request.currencyCode)
		this.logger.debug(`Successfully seeded data for ${tenantId}`)
	}

	private async seedSettings(tenantId: string, request: CreateTenantWithUserRequest): Promise<void> {
		const settings: Partial<Settings> = {
			tenantId,
			customerApp: { displayName: request.companyName, quickActions: defaultQuickActions },
			reservation: {
				autoApproveReservations: {
					enabled: false,
					guestLimitOverall: 0,
					guestLimitPerReservation: 0,
				},
				googleReservations: {
					enabled: false,
				},
				defaultReservationTime: 60,
				dashboardReservationTimeLimit: 3,
			},
			features: { reservation: true, statistics: true, kitchen: true, whatsApp: false },
			restaurant: {
				email: request.contactEmail,
				address: request.address,
				googlePlaceId: request.googlePlaceId,
				timezone: request.timezone,
			},
			kitchen: { autoStartCooking: false },
			billing: { currencyCode: request.currencyCode },
		}
		await this._settingsRepository.save(settings)
		this.logger.debug({ message: `Seeded default settings for ${tenantId}`, settings })
	}

	private async seedTables(tenantId: string, user: User): Promise<void> {
		const t1 = await this._tableRepository.save({
			tenantId,
			name: 'T1',
			capacity: 3,
			columnStart: 0,
			rowStart: 0,
			width: 2,
			height: 2,
			shape: TableShape.rectangle,
		})
		const t2 = await this._tableRepository.save({
			tenantId,
			name: 'T2',
			capacity: 3,
			columnStart: 3,
			rowStart: 0,
			width: 2,
			height: 2,
			shape: TableShape.rectangle,
		})

		await this._tableSubscriberRepository.save({ tenantId, table: t1, user })
		await this._tableSubscriberRepository.save({ tenantId, table: t2, user })

		await this._tableTokenRepository.save({ tenantId, tableId: t1.id })
		await this._tableTokenRepository.save({ tenantId, tableId: t2.id })

		this.logger.debug({
			message: `Seeded default tables, table subscriptions for user ${user.id} and table tokens for ${tenantId}`,
			t1,
			t2,
		})
	}

	private async seedMenu(tenantId: string, currency: string): Promise<void> {
		const drinks = await this._categoryRepository.save({ tenantId, name: 'Drinks', sortOrder: 1 })
		const juice = await this._categoryRepository.save({ tenantId, name: 'Juice', sortOrder: 1, parentCategory: drinks })
		const dessert = await this._categoryRepository.save({ tenantId, name: 'Dessert', sortOrder: 2 })

		const appleJuice = await this._menuItemRepository.save({
			tenantId,
			name: 'Apple Juice',
			description: 'Fresh Apple Juice',
			amount: 0.5,
			unit: ItemUnit.liter,
			currency,
			price: 4.2,
		})

		const blueberryCake = await this._menuItemRepository.save({
			tenantId,
			name: 'Blueberry Cake',
			description: 'Freshly baked Blueberry Cake',
			amount: 250,
			unit: ItemUnit.gram,
			currency,
			price: 6,
		})

		await this._itemCategoryRepository.save({ tenantId, category: drinks, item: appleJuice, sortOrder: 1 })
		await this._itemCategoryRepository.save({ tenantId, category: juice, item: appleJuice, sortOrder: 1 })
		await this._itemCategoryRepository.save({ tenantId, category: dessert, item: blueberryCake, sortOrder: 1 })

		this.logger.debug({
			message: `Seeded default categories and menu items for ${tenantId}`,
			drinks,
			juice,
			dessert,
			appleJuice,
			blueberryCake,
		})
	}
}

const defaultQuickActions: QuickAction[] = [
	{
		ackMessage: 'The waiter is on the way',
		description: 'You have a question for the waiter',
		headline: 'Question',
		iconUrl: 'https://kadi-static.s3.eu-central-1.amazonaws.com/icons/question.svg',
		messageType: MessageType.request_waiter,
		response: 'The waiter was informed',
	},
	{
		ackMessage: 'The waiter is on the way',
		description: 'Waiter should come to the table',
		headline: 'Pay',
		iconUrl: 'https://kadi-static.s3.eu-central-1.amazonaws.com/icons/pay.svg',
		messageType: MessageType.request_payment,
		response: 'The waiter was informed',
	},
	{
		ackMessage: 'The waiter takes the order right away',
		description: 'You want to order from the waiter',
		headline: 'Order',
		iconUrl: 'https://kadi-static.s3.eu-central-1.amazonaws.com/icons/order.svg',
		messageType: MessageType.request_order,
		response: 'The waiter was informed',
	},
]
